import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { transformFilteredOptions } from '@/features/vehiclesearch/utils';
import { useFuelTypes, useTransmissions } from '@/features/vehiclesearch/hooks/useVehicleNameLookups';
import type {
    FilteredOptionsResponse,
    VehicleDerivative,
    VehicleSearchFilters,
} from '@/features/vehiclesearch/types/manualsearch.types';
import { OPTIONS_ERROR_MESSAGE } from '../components/planVehicleRuleFormState';

interface Params<TInput extends FieldValues, TOutput extends FieldValues> {
    control: Control<TInput, unknown, TOutput>;
    filters: VehicleSearchFilters;
    availableOptions: FilteredOptionsResponse['available_options'];
    matchingDerivatives: VehicleDerivative[];
    isLoadingOptions: boolean;
    cascadeOptionsError: string | null;
    hasMakeAndModel: boolean;
}

// Shared by the single and batch vehicle rule forms: the lookup queries, the merged options
// error, and the memoised option lists and selected values the model and derivative pickers need.
export function usePlanVehicleRuleOptionState<TInput extends FieldValues, TOutput extends FieldValues>({
    control,
    filters,
    availableOptions,
    matchingDerivatives,
    isLoadingOptions,
    cascadeOptionsError,
    hasMakeAndModel,
}: Params<TInput, TOutput>) {
    const fuelTypesQuery = useFuelTypes();
    const transmissionsQuery = useTransmissions();
    const lookupsFailed = fuelTypesQuery.isError || transmissionsQuery.isError;
    const optionsError = cascadeOptionsError ?? (lookupsFailed ? OPTIONS_ERROR_MESSAGE : null);

    const cascadeOptions = useMemo(() => transformFilteredOptions(availableOptions), [availableOptions]);
    const modelValue = useMemo(
        () => cascadeOptions.models.find((o) => o.value === filters.model_id) ?? null,
        [cascadeOptions.models, filters.model_id],
    );
    const derivativeOptions = useMemo(
        () => matchingDerivatives.map((d) => ({ value: d.id, label: d.short_name })),
        [matchingDerivatives],
    );

    const derivativeId = useWatch({ control, name: 'derivative_id' as Path<TInput> }) as string | undefined;
    const derivativeSelected = Boolean(derivativeId);
    const derivativeValue = useMemo(
        () => derivativeOptions.find((o) => o.value === derivativeId) ?? null,
        [derivativeOptions, derivativeId],
    );

    const showNoMatchHint =
        hasMakeAndModel && !isLoadingOptions && !optionsError && matchingDerivatives.length === 0;

    return {
        fuelTypesQuery,
        transmissionsQuery,
        optionsError,
        cascadeOptions,
        modelValue,
        derivativeOptions,
        derivativeSelected,
        derivativeValue,
        showNoMatchHint,
    };
}
